/**
 * client/src/main.ts — v4 cleanup: typed, minimal, no dead code
 */

import "./styles/main.css";
import { applyLang, detectLang, toggleLang } from "./i18n";
import { PORTFOLIO, type Lang } from "./i18n/copy";
import { initStatusPage } from "./pages/status";
import { registerPwa } from "./pwa";
import {
  createDoorpostScene,
  type SceneApi,
  type ZoneId,
} from "./scene";
import { mountShell } from "./shared/shell";
import {
  enrichAsBlurb,
  initFeatureChrome,
  onZoneActivated,
  setBootLang,
  wireMinimap,
  bindFeatureApi,
  unbindFeatureApi,
} from "./systems/boot";
import { startEdgeFeed, stopEdgeFeed } from "./edge/feed";
import { mountEdgeConsole } from "./edge/console";
import {
  applyTheme,
  bootTheme,
  detectTheme,
  toggleTheme,
  watchSystemTheme,
  type Theme,
} from "./theme";

function hidePreloader(): void {
  const el = document.getElementById("preloader");
  if (!el) return;
  el.classList.add("is-done");
  window.setTimeout(() => el.remove(), 600);
}

function initHelpToggle(): void {
  const btn = document.querySelector<HTMLButtonElement>("#help-toggle");
  const card = document.getElementById("help-card");
  if (!btn || !card) return;
  const KEY = "doorpost.helpSeen";
  const openHelp = () => { card.removeAttribute("hidden"); btn.classList.add("is-on", "is-pulse"); btn.setAttribute("aria-expanded", "true"); };
  const closeHelp = (persist: boolean) => { card.setAttribute("hidden", ""); btn.classList.remove("is-on", "is-pulse"); btn.setAttribute("aria-expanded", "false"); if (persist) localStorage.setItem(KEY, "1"); };
  if (!localStorage.getItem(KEY)) {
    // open late enough that the avatar intro has played; auto-dismiss so the
    // view center (player) is never covered for long
    window.setTimeout(() => openHelp(), 2000);
    window.setTimeout(() => closeHelp(false), 2000 + 4200);
  }
  btn.addEventListener("click", () => { if (card.hasAttribute("hidden")) openHelp(); else closeHelp(true); });
  document.addEventListener("pointerdown", (e) => {
    if (card.hasAttribute("hidden")) return;
    const t = e.target as Node | null;
    if (t && (card.contains(t) || btn.contains(t))) return;
    card.setAttribute("hidden", "");
    btn.classList.remove("is-on");
    btn.setAttribute("aria-expanded", "false");
  });
}

function initJoystick(api: SceneApi): () => void {
  const root = document.getElementById("joystick");
  const knob = document.getElementById("joystick-knob");
  if (!root || !knob) return () => {};

  let active = false;
  let pid: number | null = null;
  const max = 46;

  const setKnob = (x: number, y: number) => {
    knob.style.transform = `translate(${x}px, ${y}px)`;
  };

  const read = (clientX: number, clientY: number) => {
    const rect = root.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    let dx = clientX - cx;
    let dy = clientY - cy;
    const len = Math.hypot(dx, dy) || 1;
    if (len > max) {
      dx = (dx / len) * max;
      dy = (dy / len) * max;
    }
    setKnob(dx, dy);
    const title = document.getElementById("hud-title");
    title?.classList.add("is-driving");
    let gx = dx / max;
    let gy = -dy / max;
    const mag = Math.hypot(gx, gy);
    const dead = 0.14;
    if (mag < dead) { api.setMoveInput(0, 0); return; }
    const remapped = (mag - dead) / (1 - dead);
    const curved = Math.pow(Math.min(1, remapped), 1.25);
    const scale = curved / mag;
    api.setMoveInput(gx * scale, gy * scale);
  };

  const end = () => {
    active = false;
    pid = null;
    setKnob(0, 0);
    document.getElementById("hud-title")?.classList.remove("is-driving");
    api.setMoveInput(0, 0);
  };
  const onPointerDown = (e: PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    active = true;
    pid = e.pointerId;
    root.setPointerCapture(e.pointerId);
    read(e.clientX, e.clientY);
  };
  const onPointerMove = (e: PointerEvent) => {
    if (!active || e.pointerId !== pid) return;
    e.preventDefault();
    e.stopPropagation();
    read(e.clientX, e.clientY);
  };
  const onPointerUp = (e: PointerEvent) => {
    if (e.pointerId !== pid) return;
    end();
  };

  root.addEventListener("pointerdown", onPointerDown, { passive: false });
  root.addEventListener("pointermove", onPointerMove, { passive: false });
  root.addEventListener("pointerup", onPointerUp);
  root.addEventListener("pointercancel", end);
  root.addEventListener("lostpointercapture", end);

  return () => {
    end();
    root.removeEventListener("pointerdown", onPointerDown);
    root.removeEventListener("pointermove", onPointerMove);
    root.removeEventListener("pointerup", onPointerUp);
    root.removeEventListener("pointercancel", end);
    root.removeEventListener("lostpointercapture", end);
  };
}

function zoneMeta(id: Exclude<ZoneId, null>, lang: Lang) {
  if (id === "edge") {
    return {
      acc: "EDGE",
      name: lang === "zh" ? "边缘控制台" : "Edge console",
      role: lang === "zh" ? "实测" : "Live measurements",
      blurb:
        lang === "zh"
          ? "挂在展场尽头的 /api/edge 实测面板。无模拟拓扑。"
          : "Live /api/edge panel at the far end of the plaza. No simulated topology.",
      href: null as string | null,
    };
  }
  const item = PORTFOLIO.find((p) => p.id === id);
  if (!item) return null;
  return {
    acc: item.id.toUpperCase(),
    name: lang === "zh" ? item.nameZh : item.nameEn,
    role: lang === "zh" ? item.roleZh : item.roleEn,
    blurb: enrichAsBlurb(
      lang === "zh" ? item.blurbZh : item.blurbEn,
      item.id,
    ),
    href: item.href,
  };
}

function bindZonePanel(
  api: SceneApi,
  getLang: () => Lang,
): { onZone: (id: ZoneId) => void; destroy: () => void } {
  const panel = document.getElementById("zone-panel");
  const acc = document.getElementById("zone-acc");
  const name = document.getElementById("zone-name");
  const role = document.getElementById("zone-role");
  const blurb = document.getElementById("zone-blurb");
  const link = document.getElementById("zone-link") as HTMLAnchorElement | null;
  const close = document.getElementById("zone-close");
  const title = document.getElementById("hud-title");
  let last: ZoneId = null;

  const show = (id: ZoneId) => {
    last = id;
    if (!panel) return;
    if (!id) {
      panel.hidden = true;
      title?.classList.remove("is-dim");
      return;
    }
    const meta = zoneMeta(id, getLang());
    if (!meta) {
      panel.hidden = true;
      title?.classList.remove("is-dim");
      return;
    }
    if (acc) acc.textContent = meta.acc;
    if (name) name.textContent = meta.name;
    if (role) role.textContent = meta.role;
    if (blurb) blurb.textContent = meta.blurb;
    if (link) {
      if (meta.href) {
        link.hidden = false;
        link.href = meta.href;
        try {
          link.textContent = new URL(meta.href).host.replace(/^www\./, "");
        } catch {
          link.textContent = meta.href;
        }
      } else {
        link.hidden = true;
      }
    }
    panel.hidden = false;
    title?.classList.add("is-dim");
    onZoneActivated(id);
  };
  const onClose = () => api.setActiveZone(null);
  const onLang = () => {
    if (last) show(last);
  };

  close?.addEventListener("click", onClose);
  document.addEventListener("doorpost:lang", onLang);

  return {
    onZone: show,
    destroy: () => {
      close?.removeEventListener("click", onClose);
      document.removeEventListener("doorpost:lang", onLang);
      if (panel) panel.hidden = true;
      title?.classList.remove("is-dim");
    },
  };
}

type SceneLifecycle = {
  api: SceneApi;
  dispose: () => void;
  isDisposed: () => boolean;
};

async function initScene(
  theme: Theme,
  getLang: () => Lang,
  onDisposeFeatures: () => void,
): Promise<SceneLifecycle | null> {
  const mount = document.getElementById("stage");
  if (!mount) return null;

  let onZone = (_id: ZoneId) => {};
  const api = await createDoorpostScene(mount, {
    onReady: hidePreloader,
    theme,
    onZone: (id) => onZone(id),
  });
  const resize = () => api.resize();
  window.addEventListener("resize", resize, { passive: true });

  const hud = document.getElementById("hud");
  const edgeUi = hud ? mountEdgeConsole(hud) : null;
  const zonePanel = bindZonePanel(api, getLang);
  const stopMinimap = wireMinimap(api);
  const stopJoystick = initJoystick(api);
  onZone = (id) => {
    zonePanel.onZone(id);
    edgeUi?.setNear(id === "edge");
  };
  onZone(api.getActiveZone());

  bindFeatureApi(api);
  const globals = window as unknown as {
    __doorpost?: SceneApi;
    __doorpostScene?: unknown;
  };
  globals.__doorpost = api;
  let diagnosticScene: unknown;
  if (new URLSearchParams(location.search).has("probe")) {
    try {
      diagnosticScene = api._scene?.();
      if (diagnosticScene) globals.__doorpostScene = diagnosticScene;
    } catch {
      /* diagnostics must never break boot */
    }
  }

  const hideTimer = window.setTimeout(hidePreloader, 2500);
  const disposeScene = api.dispose.bind(api);
  let disposed = false;
  const dispose = () => {
    if (disposed) return;
    disposed = true;
    window.removeEventListener("resize", resize);
    window.removeEventListener("pagehide", onPageHide);
    window.removeEventListener("unload", dispose);
    window.clearTimeout(hideTimer);
    stopMinimap();
    stopJoystick();
    zonePanel.destroy();
    edgeUi?.destroy();
    unbindFeatureApi(api);
    onDisposeFeatures();
    if (globals.__doorpost === api) delete globals.__doorpost;
    if (diagnosticScene && globals.__doorpostScene === diagnosticScene) {
      delete globals.__doorpostScene;
    }
    disposeScene();
  };
  const onPageHide = (event: PageTransitionEvent) => {
    if (!event.persisted) dispose();
  };
  api.dispose = dispose;
  window.addEventListener("pagehide", onPageHide);
  window.addEventListener("unload", dispose);

  return { api, dispose, isDisposed: () => disposed };
}

async function boot(): Promise<void> {
  if (document.getElementById("app-shell")) mountShell();

  let lang: Lang = detectLang();
  let theme: Theme = detectTheme();
  const getLang = () => lang;

  bootTheme(theme);
  applyLang(lang);
  setBootLang(lang);
  void initStatusPage();
  registerPwa();

  const isHome = document.body.dataset.page === "home" && Boolean(document.getElementById("stage"));
  let scene: SceneLifecycle | null = null;
  let stopFeatureChrome = () => {};
  let stoppedFeatures = false;
  const stopFeatures = () => {
    if (stoppedFeatures) return;
    stoppedFeatures = true;
    stopFeatureChrome();
    stopEdgeFeed();
  };
  if (isHome) {
    initHelpToggle();
    stopFeatureChrome = initFeatureChrome();
    void startEdgeFeed();
    scene = await initScene(theme, getLang, stopFeatures);
  } else {
    hidePreloader();
  }

  document
    .querySelector<HTMLButtonElement>("#lang-toggle")
    ?.addEventListener("click", () => {
      lang = toggleLang(lang);
      setBootLang(lang);
    });

  document
    .querySelector<HTMLButtonElement>("#theme-toggle")
    ?.addEventListener("click", () => {
      void toggleTheme(theme).then((next) => {
        theme = next;
        if (!scene?.isDisposed()) scene?.api.setTheme(next);
      });
    });

  const stopWatchingSystemTheme = watchSystemTheme((next) => {
    theme = next;
    void applyTheme(next).then(() => {
      if (!scene?.isDisposed()) scene?.api.setTheme(next);
    });
  });
  let stoppedWatchingSystemTheme = false;
  const stopThemeWatch = () => {
    if (stoppedWatchingSystemTheme) return;
    stoppedWatchingSystemTheme = true;
    stopWatchingSystemTheme();
  };
  const onPageHide = (event: PageTransitionEvent) => {
    if (event.persisted) return;
    stopThemeWatch();
    stopFeatures();
  };
  window.addEventListener("pagehide", onPageHide);
  window.addEventListener("unload", stopThemeWatch, { once: true });
  window.addEventListener("unload", stopFeatures, { once: true });

  document.documentElement.classList.add("is-ready");
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => void boot(), { once: true });
} else {
  void boot();
}

window.addEventListener("load", hidePreloader, { once: true });
